/*
INTELLECTUAL PROPERTY NOTICE
© Orbit Tickets Ltd. All rights reserved.
This file contains Custom / Included Intellectual Property created for and owned by the Customer as part of an application developed by Kaleida within the Platform.
Custom / Included Intellectual Property may be used, reproduced, adapted, and incorporated into any application, system, or product developed by or on behalf of the Customer, whether or not such application forms part of the Platform, subject always to the terms of the agreement and provided that such use does not include or rely upon any Protected Intellectual Property of Kaleida Ltd.
For the avoidance of doubt, the inclusion of any application within the Platform does not restrict the Customer's right to use Custom / Included Intellectual Property outside of the Platform.
*/

// IP-NOTICE: CUSTOM
import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import { MatOption } from '@angular/material/core';
import { OrderSearchListFilterModel } from 'app/modules/order/order-search/order-search/models/order-search-list-filter.model';
import { OrderSearchListService } from 'app/modules/order/order-search/order-search/services/order-search-list.service';
import { FilterFieldsBaseComponent } from 'app/shared/components/list-filter/filter-fields-base.component';
import { ListFilterIntermediaryService } from 'app/shared/components/list-filter/list-filter-intermediary-service.component';
import { FilterApplyMode, dateRangeFilterField, filterField, immediateFilterField, textFilterField } from 'app/shared/components/filter-framework/filter-framework.models';
import { canFilterMultiselect } from 'app/shared/helpers/multi-select-dropdown-helper';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { Filter, FilterOperatorType } from 'app/shared/models/filter.model';
import { LookupList } from 'app/shared/models/lookup-list.model';
import { UiStateService } from 'app/shared/services/ui-state.service';
import moment from 'moment';
import { OrderListType } from '../models/order-list-type';
import { OrderSearchListFilterResponseModel } from '../models/order-search-list-filter-response.model';

@Component({
	selector: 'order-search-list-filter',
	templateUrl: './order-search-list-filter.component.html',
	styleUrls: ['./order-search-list-filter.component.scss'],
	styles: [
		`
			:host mat-form-field {
				min-width: 245px;
				max-width: 245px;
				width: 245px;
			}
			.filter-row {
				flex-wrap: wrap;
			}
			.bottom-padding-15px {
				padding-bottom: 15px;
			}
			.no-bottom-padding {
				margin-bottom: 0;
			}
		`,
	],
	providers: [{ provide: FilterFieldsBaseComponent, useExisting: forwardRef(() => OrderSearchListFilterComponent) }],
	standalone: false,
})
export class OrderSearchListFilterComponent extends FilterFieldsBaseComponent implements OnInit {
	model: OrderSearchListFilterModel = new OrderSearchListFilterModel();
	override filterMetadata = [
		textFilterField({ id: 'orderNumber', label: 'Order number', defaultValue: this.model.orderNumber, sensitive: true }),
		textFilterField({ id: 'organiser', label: 'Organiser', defaultValue: this.model.organiser }),
		textFilterField({ id: 'event', label: 'Event', defaultValue: this.model.event }),
		filterField<number[]>({ id: 'ticketType', controlNames: ['ticketType'], label: 'Ticket type', defaultValue: this.model.ticketType, applyMode: FilterApplyMode.Immediate, persist: true, removable: true,
			isActive: value => this.isSpecificSelection(value), formatDisplayValue: value => this.formatCountedSelection(value, 'All Ticket Types') }),
		textFilterField({ id: 'venue', label: 'Venue', defaultValue: this.model.venue }),
		textFilterField({ id: 'customer', label: 'Customer', defaultValue: this.model.customer, sensitive: true }),
		dateRangeFilterField({ id: 'eventDate', label: 'Event date', fromControlName: 'eventDateFrom', toControlName: 'eventDateTo', fromDefault: this.model.eventDateFrom, toDefault: this.model.eventDateTo }),
		dateRangeFilterField({ id: 'orderDate', label: 'Order date', fromControlName: 'orderDateFrom', toControlName: 'orderDateTo', fromDefault: this.model.orderDateFrom, toDefault: this.model.orderDateTo }),
		immediateFilterField({ id: 'paymentPlan', label: 'Payment plan', defaultValue: this.model.paymentPlan }),
		filterField<number[]>({ id: 'eventGenre', controlNames: ['eventGenre'], label: 'Event genre', defaultValue: this.model.eventGenre, applyMode: FilterApplyMode.Immediate, persist: true, removable: true,
			isActive: value => this.isSpecificSelection(value), formatDisplayValue: value => this.formatLookupSelection(value, this.eventGenreLookupData, 'All Event Genres') }),
		filterField<number[]>({ id: 'eventStatus', controlNames: ['eventStatus'], label: 'Event status', defaultValue: this.model.eventStatus, applyMode: FilterApplyMode.Immediate, persist: true, removable: true,
			isActive: value => this.isSpecificSelection(value), formatDisplayValue: value => this.formatLookupSelection(value, this.eventStatusLookupData, 'All Event Statuses') }),
	];
	@Input() customerId: number;

	ticketTypeLookupData: any[];
	paymentPlanLookupData: any[];
	eventGenreLookupData: any[];
	eventStatusLookupData: any[];
	@ViewChild('allTicketTypesSelected') private allTicketTypesSelected: MatOption;
	@ViewChild('allPaymentPlansSelected') private allPaymentPlansSelected: MatOption;
	@ViewChild('allEventGenresSelected') private allEventGenresSelected: MatOption;
	@ViewChild('allEventStatusesSelected') private allEventStatusesSelected: MatOption;

	@Output() isOrganiserUser = new EventEmitter<boolean>();
	organiserUser: boolean;
	entryTicketTypeLookupData: any[] = [];
	nonEntryTicketTypeLookupData: any[] = [];
	glampingTicketTypeLookupData: any[] = [];

	get listType(): OrderListType {
		if (this.hasCustomerId) {
			return OrderListType.CustomerOrderList;
		}
		if (this.organiserUser) {
			return OrderListType.OrganiserOrderList;
		}
		return OrderListType.DefaultOrderList;
	}

	get hasCustomerId(): boolean {
		return this.customerId != null && this.customerId > 0;
	}

	get isCustomerOrderList(): boolean {
		return this.listType === OrderListType.CustomerOrderList;
	}
	get isOrganiserOrderList(): boolean {
		return this.listType === OrderListType.OrganiserOrderList;
	}
	get isDefaultOrderList(): boolean {
		return this.listType === OrderListType.DefaultOrderList;
	}

	get orderNumber(): AbstractControl {
		return this.dataForm.controls['orderNumber'];
	}
	get organiser(): AbstractControl {
		return this.dataForm.controls['organiser'];
	}
	get event(): AbstractControl {
		return this.dataForm.controls['event'];
	}
	get ticketType(): AbstractControl {
		return this.dataForm.controls['ticketType'];
	}
	get venue(): AbstractControl {
		return this.dataForm.controls['venue'];
	}
	get customer(): AbstractControl {
		return this.dataForm.controls['customer'];
	}
	get eventDateFrom(): AbstractControl {
		return this.dataForm.controls['eventDateFrom'];
	}
	get eventDateTo(): AbstractControl {
		return this.dataForm.controls['eventDateTo'];
	}
	get orderDateFrom(): AbstractControl {
		return this.dataForm.controls['orderDateFrom'];
	}
	get orderDateTo(): AbstractControl {
		return this.dataForm.controls['orderDateTo'];
	}
	get paymentPlan(): AbstractControl {
		return this.dataForm.controls['paymentPlan'];
	}
	get eventGenre(): AbstractControl {
		return this.dataForm.controls['eventGenre'];
	}
	get eventStatus(): AbstractControl {
		return this.dataForm.controls['eventStatus'];
	}

	private uiStateKey = 'OrderSearchListFilter';

	constructor(
		private fb: FormBuilder,
		private uiStateService: UiStateService,
		private service: OrderSearchListService,
		private formHelperService: ReactiveFormHelperService,
		protected listFilterIntermediaryService: ListFilterIntermediaryService,
	) {
		super(listFilterIntermediaryService);
	}
	createGroupedLookupData(list: LookupList): any[] {
		let model: LookupList[] = [];

		let entryTickets = {} as LookupList;
		let nonEntryTickets = {} as LookupList;
		let glampingTickets = {} as LookupList;

		entryTickets.name = 'Entry';
		entryTickets.items = list.items
			.filter((item) => item.data.entryTicketType === true)
			.map((item) => {
				return item;
			});

		nonEntryTickets.name = 'Non Entry';
		nonEntryTickets.items = list.items
			.filter((item) => item.data.nonEntryTicketType === true)
			.map((item) => {
				return item;
			});

		glampingTickets.name = 'Glamping';
		glampingTickets.items = list.items
			.filter((item) => item.data.glampingTicketType === true)
			.map((item) => {
				return item;
			});

		if (entryTickets.items.length > 0) {
			model.push(entryTickets);
		}
		if (nonEntryTickets.items.length > 0) {
			model.push(nonEntryTickets);
		}
		if (glampingTickets.items.length > 0) {
			model.push(glampingTickets);
		}

		this.entryTicketTypeLookupData = entryTickets.items;
		this.nonEntryTicketTypeLookupData = nonEntryTickets.items;
		this.glampingTicketTypeLookupData = glampingTickets.items;

		return model;
	}

	ngOnInit() {
		const model = this.getModelFromStorageOrDefault();

		this.service.getFilterLookupData().then((response: OrderSearchListFilterResponseModel) => {
			this.ticketTypeLookupData = this.createGroupedLookupData(response.ticketTypeLookupData);
			this.paymentPlanLookupData = this.formHelperService.createLookupData(response.paymentPlanLookupData);
			this.eventGenreLookupData = this.formHelperService.createLookupData(response.eventGenreLookupData);
			this.eventStatusLookupData = this.formHelperService.createLookupData(response.eventStatusLookupData);

			this.organiserUser = response.isOrganiserUser;
			this.isOrganiserUser.emit(response.isOrganiserUser);
			this.restoreTicketTypeSelectionFromModel(model);
			this.checkAllMultiSelectsForAllValue();
		});

		this.dataForm = this.fb.group({
			orderNumber: [model.orderNumber],
			organiser: [model.organiser],
			event: [model.event],
			ticketType: [model.ticketType],
			venue: [model.venue],
			customer: [model.customer],
			eventDateFrom: [model.eventDateFrom],
			eventDateTo: [model.eventDateTo],
			orderDateFrom: [model.orderDateFrom],
			orderDateTo: [model.orderDateTo],
			paymentPlan: [model.paymentPlan],
			eventGenre: [model.eventGenre],
			eventStatus: [model.eventStatus],
		});
		this.ticketType.valueChanges.subscribe(() => {
			this.syncSubsetSelection();
		});
		this.dataForm.markAsPristine();
	}

	ngAfterViewInit() {
		this.notifyFilterChanged();
	}

	private checkAllMultiSelectsForAllValue() {
		this.checkIfMultiSelectHaveAllSelected(this.paymentPlan, this.paymentPlanLookupData, this.allPaymentPlansSelected);
		this.checkIfMultiSelectHaveAllSelected(this.eventGenre, this.eventGenreLookupData, this.allEventGenresSelected);
		this.checkIfMultiSelectHaveAllSelected(this.eventStatus, this.eventStatusLookupData, this.allEventStatusesSelected);
	}

	private isSpecificSelection(value: number[]): boolean { return Array.isArray(value) && value.length > 0 && !value.includes(0); }
	private formatCountedSelection(value: number[], allLabel: string): string {
		if (!value?.length || value.includes(0)) return allLabel;
		return `${this.getValidSelectedIds(value).length} selected`;
	}
	private formatLookupSelection(value: number[], lookupData: any[], allLabel: string): string {
		if (!value?.length || value.includes(0)) return allLabel;
		const labels = value.map(id => lookupData?.find(item => item.id === id)?.text).filter(Boolean);
		return labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`;
	}

	resetToDefault() {
		this.dataForm.controls['orderNumber'].setValue(this.model.orderNumber);
		this.dataForm.controls['organiser'].setValue(this.model.organiser);
		this.dataForm.controls['event'].setValue(this.model.event);
		this.dataForm.controls['ticketType'].setValue(this.model.ticketType);
		this.dataForm.controls['venue'].setValue(this.model.venue);
		this.dataForm.controls['customer'].setValue(this.model.customer);
		this.dataForm.controls['eventDateFrom'].setValue(this.model.eventDateFrom);
		this.dataForm.controls['eventDateTo'].setValue(this.model.eventDateTo);
		this.dataForm.controls['orderDateFrom'].setValue(this.model.orderDateFrom);
		this.dataForm.controls['orderDateTo'].setValue(this.model.orderDateTo);
		this.dataForm.controls['paymentPlan'].setValue(this.model.paymentPlan);
		this.dataForm.controls['eventGenre'].setValue(this.model.eventGenre);
		this.dataForm.controls['eventStatus'].setValue(this.model.eventStatus);
		this.restoreTicketTypeSelectionFromModel(this.model);
		this.checkAllMultiSelectsForAllValue();
	}

	private persistFilterModel() {
		const stateModel = new OrderSearchListFilterModel(
			this.orderNumber.value,
			this.organiser.value,
			this.event.value,
			this.ticketType.value,
			this.venue.value,
			this.customer.value,
			this.eventDateFrom.value,
			this.eventDateTo.value,
			this.orderDateFrom.value,
			this.orderDateTo.value,
			this.paymentPlan.value,
			this.eventGenre.value,
			this.eventStatus.value,
		);
		this.uiStateService.setUiState(this.uiStateKey, stateModel);
	}

	getFilters(): Array<Filter> {
		this.persistFilterModel();
		const filters = new Array<Filter>();

		if (this.orderNumber.value) {
			filters.push(new Filter('OrderNumber', FilterOperatorType.Contains, this.orderNumber.value));
		}

		if (!this.isOrganiserOrderList && this.organiser.value) {
			filters.push(new Filter('Organiser', FilterOperatorType.Contains, this.organiser.value));
		}
		if (this.event.value) {
			filters.push(new Filter('Event', FilterOperatorType.Contains, this.event.value));
		}
		if (canFilterMultiselect('ticketType', this.dataForm)) {
			filters.push(new Filter('TicketType', FilterOperatorType.Equals, this.getValidSelectedIds(this.ticketType.value)));
		}
		if (this.venue.value) {
			filters.push(new Filter('Venue', FilterOperatorType.Contains, this.venue.value));
		}
		if (!this.isCustomerOrderList && this.customer.value) {
			filters.push(new Filter('CustomerName', FilterOperatorType.Contains, this.customer.value));
		}
		if (this.isCustomerOrderList) {
			filters.push(new Filter('CustomerId', FilterOperatorType.Equals, this.customerId));
		}
		if (this.eventDateFrom.value) {
			const date = moment(this.eventDateFrom.value).startOf('day').toDate();
			filters.push(new Filter('EventDate', FilterOperatorType.GreaterThanEqual, date));
		}
		if (this.eventDateTo.value) {
			const date = moment(this.eventDateTo.value).endOf('day').toDate();
			filters.push(new Filter('EventDate', FilterOperatorType.LessThanEqual, date));
		}
		if (this.orderDateFrom.value) {
			const date = moment(this.orderDateFrom.value).startOf('day').toDate();
			filters.push(new Filter('OrderDate', FilterOperatorType.GreaterThanEqual, date));
		}
		if (this.orderDateTo.value) {
			const date = moment(this.orderDateTo.value).endOf('day').toDate();
			filters.push(new Filter('OrderDate', FilterOperatorType.LessThanEqual, date));
		}
		if (this.paymentPlan.value && this.paymentPlan.value !== 0) {
			filters.push(new Filter('PaymentPlanEnum', FilterOperatorType.Equals, this.paymentPlan.value));
		}
		if (canFilterMultiselect('eventGenre', this.dataForm)) {
			filters.push(new Filter('EventGenre', FilterOperatorType.Equals, this.eventGenre.value));
		}
		if (canFilterMultiselect('eventStatus', this.dataForm)) {
			filters.push(new Filter('EventStatus', FilterOperatorType.Equals, this.eventStatus.value));
		}

		return filters;
	}

	private getModelFromStorageOrDefault(): OrderSearchListFilterModel {
		let model = this.uiStateService.getUiStateForKey(this.uiStateKey);

		if (model == null) {
			model = new OrderSearchListFilterModel();
		}

		return model;
	}

	toggleAllGrouped() {
		const allIds = [...this.entryTicketTypeLookupData.map((i) => i.id), ...this.nonEntryTicketTypeLookupData.map((i) => i.id), ...this.glampingTicketTypeLookupData.map((i) => i.id)];
		const current = new Set<number>(this.ticketType.value ?? []);
		const allSelected = allIds.every((id) => current.has(id));

		if (allSelected) {
			allIds.forEach((id) => current.delete(id));
			current.delete(0);
			current.delete(-1);
			current.delete(-2);
			current.delete(-3);
		} else {
			allIds.forEach((id) => current.add(id));
			current.add(0);
			if (this.entryTicketTypeLookupData.length) current.add(-1);
			if (this.nonEntryTicketTypeLookupData.length) current.add(-2);
			if (this.glampingTicketTypeLookupData.length) current.add(-3);
		}

		this.ticketType.setValue([...current]);
		this.ticketType.markAsTouched();
	}

	toggleGroupedSubset(subsetItems: any[], dummyValue: number) {
		const subsetIds = subsetItems.map((i) => i.id);
		const current = new Set<number>(this.ticketType.value ?? []);
		const isSubsetSelected = subsetIds.every((id) => current.has(id));

		if (isSubsetSelected) {
			subsetIds.forEach((id) => current.delete(id));
			current.delete(dummyValue);
		} else {
			subsetIds.forEach((id) => current.add(id));
			current.add(dummyValue);
		}

		// Sync All Types if all subsets are selected
		this.syncAllTypesSelection(current);

		this.ticketType.setValue([...current]);
		this.ticketType.markAsTouched();
	}

	private syncAllTypesSelection(current: Set<number>) {
		const allEntrySelected = this.entryTicketTypeLookupData.every((i) => current.has(i.id));
		const allNonEntrySelected = this.nonEntryTicketTypeLookupData.every((i) => current.has(i.id));
		const allGlampingSelected = this.glampingTicketTypeLookupData.every((i) => current.has(i.id));

		if (allEntrySelected && allNonEntrySelected && allGlampingSelected) {
			current.add(0); // All Types
		} else {
			current.delete(0);
		}
	}

	private syncSubsetSelection() {
		const current = new Set<number>(this.ticketType.value ?? []);

		if (this.entryTicketTypeLookupData.length && this.entryTicketTypeLookupData.every((i) => current.has(i.id))) current.add(-1);
		else current.delete(-1);

		if (this.nonEntryTicketTypeLookupData.length && this.nonEntryTicketTypeLookupData.every((i) => current.has(i.id))) current.add(-2);
		else current.delete(-2);

		if (this.glampingTicketTypeLookupData.length && this.glampingTicketTypeLookupData.every((i) => current.has(i.id))) current.add(-3);
		else current.delete(-3);

		this.syncAllTypesSelection(current);
		this.ticketType.setValue([...current], { emitEvent: false });
	}

	private restoreTicketTypeSelectionFromModel(model: OrderSearchListFilterModel): void {
		const allTicketIds = [...this.entryTicketTypeLookupData.map((item) => item.id), ...this.nonEntryTicketTypeLookupData.map((item) => item.id), ...this.glampingTicketTypeLookupData.map((item) => item.id)];
		const restoredValue = this.getNormalizedGroupedTicketTypeValue(model.ticketType, allTicketIds);
		this.ticketType.setValue(restoredValue, { emitEvent: false });
	}

	private getNormalizedGroupedTicketTypeValue(value: any, allTicketIds: number[]): number[] {
		const validIds = new Set(allTicketIds);
		const normalizedSelection = new Set(this.getValidSelectedIds(value).filter((id) => validIds.has(id)));

		if (Array.isArray(value) && value.includes(0)) {
			allTicketIds.forEach((id) => normalizedSelection.add(id));
		}

		if (this.entryTicketTypeLookupData.length && this.entryTicketTypeLookupData.every((item) => normalizedSelection.has(item.id))) {
			normalizedSelection.add(-1);
		}

		if (this.nonEntryTicketTypeLookupData.length && this.nonEntryTicketTypeLookupData.every((item) => normalizedSelection.has(item.id))) {
			normalizedSelection.add(-2);
		}

		if (this.glampingTicketTypeLookupData.length && this.glampingTicketTypeLookupData.every((item) => normalizedSelection.has(item.id))) {
			normalizedSelection.add(-3);
		}

		if (allTicketIds.length > 0 && allTicketIds.every((id) => normalizedSelection.has(id))) {
			normalizedSelection.add(0);
		}

		return [...normalizedSelection];
	}

	private getValidSelectedIds(value: any): number[] {
		if (!Array.isArray(value)) {
			return [];
		}

		return value
			.filter((item) => item != null && item !== 0 && item !== '')
			.map((item) => +item)
			.filter((item) => Number.isInteger(item) && item > 0);
	}
}
